import dayjs from "dayjs";

import {
  faCheck,
  faEdit,
  faUserAltSlash,
  faUserClock,
  faUserEdit
} from "@fortawesome/free-solid-svg-icons";

import {
  calcUtilitiesFromScheduleEndpoints,
  findScheduleEndpointsCommuteEmissionsTons
} from "@aclymatepackages/calcs/recurring";
import { calcAvgEmissionsPastSixMonths } from "@aclymatepackages/date-helpers";
import { mainTheme } from "@aclymatepackages/themes";
import { splitAggregatedTransactionsBreakdownSubcategories } from "@aclymatepackages/other-helpers";

import utc from "dayjs/plugin/utc";

import {
  useAccountData,
  useCachedFirebaseCrud,
  useCollectionDataListener
} from "../firebase";
import { fetchOurApi } from "../utils/apiCalls";
import { getAccountId } from "../accountHelpers";
import useDisplayMeasurementSystem from "../hooks/displayMeasurementSystem";
import usePlatformLayoutContext from "../contexts/platformLayout";
import {
  groupRollupsV2EmployeeDocsByEmployee,
  shouldUseRollupsV2Read
} from "./rollupsV2";

dayjs.extend(utc);

export const PLACEHOLDER_EMAIL_SUFFIX = "@no-email.local";

export const isPlaceholderEmail = (email = "") =>
  email.toLowerCase().endsWith(PLACEHOLDER_EMAIL_SUFFIX);

export const displayableEmail = (email = "") =>
  isPlaceholderEmail(email) ? "No email provided" : email;

export const employeeStatuses = {
  confirmed: {
    id: "confirmed",
    icon: faCheck,
    tooltip:
      "We have all of the information that we need to track this employee's emissions",
    color: mainTheme.palette.secondary.main,
    severity: 2,
    name: "Confirmed Employees"
  },
  unconfirmed: {
    id: "unconfirmed",
    icon: faUserEdit,
    tooltip:
      "You've sent this employee a survey but they haven't filled it out yet. In the meantime, we will display default emissions for this employee.",
    color: mainTheme.palette.error.main,
    severity: 4,
    name: "Unconfirmed Employees"
  },
  incomplete: {
    id: "incomplete",
    icon: faEdit,
    tooltip:
      "This employee's information is incomplete. Please send them another survey. In the meantime, we will display their most recent emissions.",
    color: mainTheme.palette.error.light,
    severity: 3,
    name: "Incomplete Employees"
  },
  onLeave: {
    id: "onLeave",
    icon: faUserClock,
    tooltip: "This employee is currently on leave",
    color: mainTheme.palette.secondary.light,
    severity: 1,
    name: "Employees on Leave"
  },
  terminated: {
    id: "terminated",
    icon: faUserAltSlash,
    tooltip: "This employee no longer works for you",
    color: mainTheme.palette.error.dark,
    severity: 0,
    name: "Terminated Employees"
  }
};

export const sendEmployeeSurvey = async ({
  setSendSurveyLoading,
  employee
}) => {
  if (isPlaceholderEmail(employee?.email)) {
    return { skipped: true, reason: "placeholder-email" };
  }

  const companyId = getAccountId();

  if (setSendSurveyLoading) {
    setSendSurveyLoading(true);
  }

  await fetchOurApi({
    path: `/survey-emails/employee/invite`,
    method: "POST",
    data: {
      employee,
      companyId
    }
  });

  if (setSendSurveyLoading) {
    return setSendSurveyLoading(false);
  }

  return null;
};

const hasRequiredScheduleParameters = (employeeData, scheduledTime) => {
  return employeeData && scheduledTime;
};

const normalizeEmployeeData = (employeeData) => {
  return Array.isArray(employeeData) ? employeeData : [employeeData];
};

const hasEmployees = (employees) => {
  return employees.length > 0;
};

const hasRequiredEmployeeFields = (employee) => {
  return employee.id && employee.email && employee.name;
};

const validateEmployeeFields = (employee) => {
  if (!hasRequiredEmployeeFields(employee)) {
    throw new Error(
      "Invalid employee data: missing required fields (id, email, or name)"
    );
  }
  return {
    id: employee.id,
    email: employee.email,
    name: employee.name
  };
};

const buildScheduleApiPayload = (
  validatedEmployees,
  companyId,
  scheduledTime,
  timezone
) => {
  return {
    employees: validatedEmployees,
    companyId,
    scheduledTime,
    timezone: timezone || "America/Los_Angeles",
    status: "pending",
    createdAt: new Date().toISOString()
  };
};

const isApiResponseSuccess = (response) => {
  return response && !response.error;
};

export const scheduleSurveyDelivery = async ({
  employeeData,
  scheduledTime,
  timezone
}) => {
  const companyId = getAccountId();

  if (!hasRequiredScheduleParameters(employeeData, scheduledTime)) {
    throw new Error(
      "Missing required parameters for scheduling survey delivery"
    );
  }

  const employees = normalizeEmployeeData(employeeData);

  if (!hasEmployees(employees)) {
    throw new Error("No employees provided for survey scheduling");
  }

  const validatedEmployees = employees.map(validateEmployeeFields);

  const response = await fetchOurApi({
    path: `/survey-emails/employee/schedule`,
    method: "POST",
    data: buildScheduleApiPayload(
      validatedEmployees,
      companyId,
      scheduledTime,
      timezone
    )
  });

  if (!isApiResponseSuccess(response)) {
    throw new Error(response?.error || "Failed to schedule survey delivery");
  }

  return {
    success: true,
    scheduledJobId: response.jobId || response.scheduledJobId || response.id,
    scheduledTime: response.scheduledTime || scheduledTime,
    employeeCount: validatedEmployees.length,
    timezone: response.timezone || timezone || "America/Los_Angeles",
    ...response
  };
};

export const modifyEmployeeVehiclesInCommuteSchedules = ({
  commuteSchedules = [],
  newVehicle,
  updatedVehicles
}) =>
  commuteSchedules.map((commuteSchedule) => {
    const newCommuteEndpoint = commuteSchedule.commuteEndpoints.map(
      (commuteEndpoint) => {
        const { vehicles: commuteEndpointVehicles = [] } = commuteEndpoint;

        const newCommuteEndpointsVehicles = commuteEndpointVehicles.map(
          (commuteVehicle) => {
            const { id: commuteVehicleId } = commuteVehicle;
            const findCommuteVehicle = updatedVehicles
              ? updatedVehicles.find(
                  ({ id: vehicleId }) => vehicleId === commuteVehicleId
                )
              : newVehicle.id === commuteVehicleId;

            if (findCommuteVehicle) {
              const {
                name: modifiedCommuteVehicleName,
                tonsCo2ePerMile: modifiedCommuteVehicleTonsCo2ePerMile
              } = updatedVehicles ? findCommuteVehicle : newVehicle;

              return {
                ...commuteVehicle,
                id: commuteVehicleId,
                name: modifiedCommuteVehicleName,
                tonsCo2ePerMile: modifiedCommuteVehicleTonsCo2ePerMile
              };
            }

            return commuteVehicle;
          }
        );

        const commuteEndpointVehiclesUpdateObj =
          newCommuteEndpointsVehicles.length
            ? { vehicles: newCommuteEndpointsVehicles }
            : {};

        return {
          ...commuteEndpoint,
          ...commuteEndpointVehiclesUpdateObj
        };
      }
    );

    return {
      ...commuteSchedule,
      commuteEndpoints: newCommuteEndpoint
    };
  });

export const buildNewEmployeeStatusLinks = (
  { links },
  newStatus,
  givenCompanyId
) => {
  const companyId = getAccountId();

  return links.map((link) =>
    link.id === (givenCompanyId || companyId)
      ? { ...link, employeeStatus: newStatus }
      : link
  );
};

// Mirrors functions/utils/individuals/employeeLinks.js's isNonEmployeeLink.
// Aclymate staff shared logins (e.g. fairware@aclymate.com) get linked to the
// company on sign-in with no employeeStatus, which the recurring calcs would
// otherwise count as an unconfirmed default employee.
const INTERNAL_LOGIN_EMAIL_DOMAIN = "@aclymate.com";

// Aclymate's own account, where @aclymate.com users are real staff. Mirrors
// ACLYMATE_COMPANY_IDS in functions/utils/individuals/employeeLinks.js (the
// front end and functions share no in-repo code), which documents why this
// is a hardcoded id.
export const ACLYMATE_COMPANY_IDS = ["H9YZ3rdg3nDf1pgH3h45"];

export const isNonEmployeeLink = (employee = {}, companyId) => {
  const { links = [], email } = employee;
  const companyLink = links.find(({ id }) => id === companyId);
  if (!companyLink) return false;
  if (companyLink.isEmployee === false) return true;
  if (companyLink.employeeStatus) return false;
  if (ACLYMATE_COMPANY_IDS.includes(companyId)) return false;
  return (
    typeof email === "string" &&
    email.trim().toLowerCase().endsWith(INTERNAL_LOGIN_EMAIL_DOMAIN)
  );
};

export const useEmployeeStatuses = () => {
  const companyId = getAccountId();
  const [employees, employeesLoading] = useCollectionDataListener("employees");

  const countedEmployees = employees.filter(
    (employee) => !isNonEmployeeLink(employee, companyId)
  );

  const employeesWithStatuses = countedEmployees.map((employee = {}) => {
    const { links = [] } = employee;
    const { employeeStatus } = links.find(({ id }) => id === companyId) || {};

    return { ...employee, status: employeeStatus };
  });

  return [employeesWithStatuses, employeesLoading];
};

// Phase 3 read-cutover (per-company canary), same rollupsV2ReadEnabled flag
// and pattern as EmissionsContextProvider's vendor listener -- see
// rollupsV2.js's groupRollupsV2EmployeeDocsByEmployee for why employees
// needs its own grouping function (employeeId -> individualId rename) and
// helpers/contexts/emissions.js's vendor block for why the monthKey >=
// startDate filter only applies on the rollups-v2 side: nothing upstream on
// that write path excludes a transaction for predating the company's
// startDate, whereas employees-aggregated-transactions is populated by the
// aggregation cron's own date >= startDate query and so never needs it.
//
// Exported (not inlined into useFindCurrentEmployeeEmissions below) so every
// consumer of per-employee aggregated transactions shares one source
// decision and one Firestore listener -- EmployeesView.js's selected-employee
// detail slider used to run its own separate, always-legacy
// useCollectionDataListener("employees-aggregated-transactions") here, which
// meant a rollupsV2ReadEnabled company's employee list (via
// useFindCurrentEmployeeEmissions) and its detail slider could show two
// different totals after an edit, since V2 updates atomically while the
// legacy aggregation cron does not.
export const useEmployeesAggregatedTransactions = () => {
  const [{ startDate, rollupsV2ReadEnabled }] = useAccountData();
  const { viewMode } = usePlatformLayoutContext() || {};
  const effectiveRollupsV2ReadEnabled = shouldUseRollupsV2Read(
    rollupsV2ReadEnabled,
    viewMode
  );

  const employeeRollupsQueries = effectiveRollupsV2ReadEnabled
    ? [["monthKey", ">=", dayjs.utc(startDate).format("YYYY-MM")]]
    : [];
  const [rawEmployeeRollups, transactionsLoading] = useCollectionDataListener(
    effectiveRollupsV2ReadEnabled
      ? "rollups-v2-employees"
      : "employees-aggregated-transactions",
    employeeRollupsQueries
  );
  const employeesAggregatedTransactions = effectiveRollupsV2ReadEnabled
    ? groupRollupsV2EmployeeDocsByEmployee(rawEmployeeRollups)
    : rawEmployeeRollups;

  return [employeesAggregatedTransactions, transactionsLoading];
};

// Returns [findCurrentEmployeeEmissions, employeeTransactionsLoading] -- the
// loading flag has to come out alongside the callback, not be swallowed
// here, so a caller (EmissionsContextProvider) can gate on it exactly like
// it already does for matchedVendorTransactionsLoading. Skipping that gate
// is what let a company mid-transition (rollupsV2ReadEnabled flips true
// before the new rollups-v2-employees listener's first snapshot arrives)
// briefly feed still-legacy-shaped docs into
// groupRollupsV2EmployeeDocsByEmployee, which expects the V2 shape.
export const useFindCurrentEmployeeEmissions = () => {
  const [employeesAggregatedTransactions, employeeTransactionsLoading] =
    useEmployeesAggregatedTransactions();
  const { convertCarbonUnits } = useDisplayMeasurementSystem();

  const findCurrentEmployeeEmissions = ({
    employees,
    emissionsMonths,
    employeeCount
  }) => {
    if (!employees.length) {
      return [];
    }

    const filterEmployeesByStatus = (status) =>
      employees.filter((employee) => employee.status === status);

    const confirmedEmployees = filterEmployeesByStatus("confirmed");
    const unconfirmedEmployees = filterEmployeesByStatus("unconfirmed");
    const terminatedEmployees = filterEmployeesByStatus("terminated");
    const onLeaveEmployees = filterEmployeesByStatus("onLeave");
    const incompleteEmployees = filterEmployeesByStatus("incomplete");

    const notUnconfirmedEmployeesCount =
      employees.length - unconfirmedEmployees.length;

    const sumEndpointTotalTons = (endpoints) =>
      endpoints
        .filter((data) => data)
        .reduce((sum, { totalTons }) => totalTons + sum, 0);

    const findCurrentScheduleMonthlyEmissions = ({ commuteSchedules }) => {
      const defaultReturnObj = {
        monthlyUtilitiesEmissionsVolume: 0,
        monthlyCommuteEmissionsVolume: 0
      };

      if (!commuteSchedules?.length) {
        return defaultReturnObj;
      }

      const schedulesWithEndpoints = commuteSchedules.filter(
        ({ commuteEndpoints }) => commuteEndpoints?.length
      );

      const [currentSchedule] = schedulesWithEndpoints;
      if (!currentSchedule) {
        return defaultReturnObj;
      }
      const { home, commuteEndpoints } = currentSchedule;
      if (!home) {
        return defaultReturnObj;
      }

      const monthlyUtilitiesEmissionsTonsArray =
        calcUtilitiesFromScheduleEndpoints({ commuteEndpoints, ...home });

      const monthlyUtilitiesEmissionsTons = sumEndpointTotalTons(
        monthlyUtilitiesEmissionsTonsArray
      );

      const { address } = home;
      const monthlyCommuteEmissionsTonsArray =
        findScheduleEndpointsCommuteEmissionsTons({
          commuteEndpoints,
          employeeAddress: address
        });

      const monthlyCommuteEmissionsTons = sumEndpointTotalTons(
        monthlyCommuteEmissionsTonsArray
      );

      return {
        monthlyUtilitiesEmissionsVolume: convertCarbonUnits(
          monthlyUtilitiesEmissionsTons
        ),
        monthlyCommuteEmissionsVolume: convertCarbonUnits(
          monthlyCommuteEmissionsTons
        )
      };
    };

    const findOnLeaveEmployeesMostRecentEmissions = (employee) => {
      const { monthlyUtilitiesEmissionsVolume, monthlyCommuteEmissionsVolume } =
        findCurrentScheduleMonthlyEmissions(employee);

      const totalMonthlyVolume =
        monthlyCommuteEmissionsVolume + monthlyUtilitiesEmissionsVolume;

      return {
        ...employee,
        mostRecentEmissionsVolume: totalMonthlyVolume,
        totalMonthlyVolume
      };
    };

    const balanceEmployeeEmissions = [
      ...confirmedEmployees,
      ...incompleteEmployees
    ].map((employee) => {
      const { id } = employee;

      const { monthlyUtilitiesEmissionsVolume, monthlyCommuteEmissionsVolume } =
        findCurrentScheduleMonthlyEmissions(employee);

      const { aggregatedTransactions = [] } =
        employeesAggregatedTransactions.find(
          ({ individualId }) => individualId === id
        ) || {};

      const employeeTransactions =
        splitAggregatedTransactionsBreakdownSubcategories(
          aggregatedTransactions
        );

      const calcTransactionsAverageVolumeBySubcategory = (subcategory) =>
        convertCarbonUnits(
          calcAvgEmissionsPastSixMonths(
            employeeTransactions.filter(
              (transaction) => transaction.subcategory === subcategory
            )
          )
        );

      const flightsTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("flights");
      const rentalCarTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("rental-cars");
      const ridesTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("rides");
      const mileageTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("company-vehicles");
      const fuelTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("fuel");
      const hotelTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("hotels");
      const trainsTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("trains");
      const equipmentFuelTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("equipment-fuel");
      const shippingTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("shipping");

      const transactionsAverageMonthlyEmissionsVolume = convertCarbonUnits(
        calcAvgEmissionsPastSixMonths(employeeTransactions)
      );

      const averageMonthlyNonCommuteEmissionsVolume =
        transactionsAverageMonthlyEmissionsVolume +
        monthlyUtilitiesEmissionsVolume;

      return {
        ...employee,
        averageMonthlyNonCommuteEmissionsVolume,
        monthlyUtilitiesEmissionsVolume,
        monthlyCommuteEmissionsVolume,
        avgMonthlyFlightsEmissions: flightsTransactionsAverage,
        avgMonthlyRentalCarEmissions: rentalCarTransactionsAverage,
        avgMonthlyRidesEmissions: ridesTransactionsAverage,
        avgMonthlyMileageEmissions: mileageTransactionsAverage,
        avgMonthlyFuelEmissions: fuelTransactionsAverage,
        avgMonthlyHotelEmissions: hotelTransactionsAverage,
        avgMonthlyTrainsEmissions: trainsTransactionsAverage,
        avgMonthlyEquipmentFuelEmissions: equipmentFuelTransactionsAverage,
        avgMonthlyShippingEmissions: shippingTransactionsAverage,
        totalMonthlyVolume:
          monthlyCommuteEmissionsVolume +
          monthlyUtilitiesEmissionsVolume +
          transactionsAverageMonthlyEmissionsVolume
      };
    });

    const onLeaveEmployeeEmissions = onLeaveEmployees.map((employee) =>
      findOnLeaveEmployeesMostRecentEmissions(employee)
    );

    const defaultEmployeesCount = employeeCount
      ? employeeCount - notUnconfirmedEmployeesCount
      : unconfirmedEmployees.length;

    const { defaultEmissions } = emissionsMonths[0] || {};
    const { monthlyEmployeesEmissionsTons, totalMonthlyTons } =
      defaultEmissions || {};

    const unconfirmedEmployeeDefaultVolume = convertCarbonUnits(
      (monthlyEmployeesEmissionsTons || totalMonthlyTons) /
        defaultEmployeesCount
    );

    const unconfirmedEmployeesDefaultEmissions = unconfirmedEmployees.map(
      (employee) => ({
        ...employee,
        defaultTotalEmissions: unconfirmedEmployeeDefaultVolume,
        totalMonthlyVolume: unconfirmedEmployeeDefaultVolume
      })
    );

    return [
      ...terminatedEmployees.map((employee) => ({
        ...employee,
        totalMonthlyVolume: 0
      })),
      ...onLeaveEmployeeEmissions,
      ...unconfirmedEmployeesDefaultEmissions,
      ...balanceEmployeeEmissions
    ].map((employee) => {
      const { status } = employee;
      const { severity } = employeeStatuses[status];

      return { ...employee, severity };
    });
  };

  return [findCurrentEmployeeEmissions, employeeTransactionsLoading];
};

export const editMostRecentSchedule = (commuteSchedules, date) =>
  commuteSchedules.map((schedule, idx) =>
    idx === 0
      ? { endDate: dayjs(date).subtract(1, "day").toDate(), ...schedule }
      : schedule
  );

export const useTerminateEmployee = () => {
  const { updateCollectionDoc } = useCachedFirebaseCrud();

  return (employee, date) => {
    const { id, commuteSchedules, links } = employee;
    const companyId = getAccountId();

    const updatedLinks = links.map((link) =>
      link.id !== companyId
        ? link
        : {
            ...link,
            status: "inactive",
            employeeStatus: "terminated",
            endDate: date
          }
    );

    return updateCollectionDoc("employees", id, {
      commuteSchedules: [...editMostRecentSchedule(commuteSchedules, date)],
      links: updatedLinks,
      endDate: date
    });
  };
};
